import { useState, useEffect, useMemo } from 'react';
import { getAllKnowledgeItems } from '../services/storage/knowledgeItemService';
import { 
  calculateLearningCurve, 
  calculateRetentionRate, 
  calculateSkillDistribution, 
  getGradeHistogramByType,
  identifyWeakAreas,
  analyzeTacticalThemeProficiency,
  calculateTypedLearningCurve
} from '../services/learning/learningMetricsService';
import { KnowledgeItem, LearningMetrics } from '../types/learning';
import { isBefore } from 'date-fns';

/**
 * useLearningAnalytics
 *
 * Custom hook to fetch and calculate learning analytics for the dashboard.
 * Returns loading, error, metrics, and dueForReview items.
 * Optimized with memoization for expensive calculations.
 */
export function useLearningAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [items, setItems] = useState<KnowledgeItem[]>([]);

  // Fetch data only once on component mount
  useEffect(() => {
    let isMounted = true;
    
    async function fetchData() {
      try {
        const fetchedItems = getAllKnowledgeItems();
        
        if (!isMounted) return;
        
        if (!Array.isArray(fetchedItems)) {
          setError(new TypeError('Knowledge items is not an array'));
          setItems([]);
        } else {
          setItems(fetchedItems);
        }
      } catch (e) {
        if (!isMounted) return;
        setError(e as Error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize metrics calculations to prevent recalculations on every render
  const metrics: LearningMetrics | null = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return null;
    
    return {
      learningCurve: calculateLearningCurve(items),
      retentionRate: calculateRetentionRate(items),
      skillDistribution: calculateSkillDistribution(items),
      gradeHistogramByType: getGradeHistogramByType(items),
      weakAreas: identifyWeakAreas(items),
      tacticalThemeProficiency: analyzeTacticalThemeProficiency(items),
      sequenceLearningCurve: calculateTypedLearningCurve(items, 'sequence')
    };
  }, [items]);

  // Memoize due items to prevent filtering on every render
  const dueForReview = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return [];
    
    const now = new Date();
    return items
      .filter(item => item.nextReviewDate && isBefore(new Date(item.nextReviewDate), now))
      .sort((a, b) => {
        // Sort by date, oldest first
        const dateA = new Date(a.nextReviewDate);
        const dateB = new Date(b.nextReviewDate);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 10); // Limit to 10 items for performance
  }, [items]);

  return { loading, error, metrics, dueForReview };
} 